/**
 * Location matching utility for SVG plan navigation.
 * Handles Roman numeral parsing, range expansion, orientation stripping, and matching.
 */

const ROMAN_VALUES: { [key: string]: number } = {
  'I': 1, 'V': 5, 'X': 10, 'L': 50, 'C': 100, 'D': 500, 'M': 1000
};

const ORIENTATION_PATTERN = /[nesw]/gi;
const RANGE_PATTERN = /^([IVXLCDM]+)-([IVXLCDM]+)$/i;

/**
 * Strips orientation characters (n, e, s, w) from a location code.
 * E.g., "XIw" -> "XI", "Ve" -> "V", "IIIn" -> "III"
 */
export function stripOrientation(code: string): string {
  if (!code) return code;
  return code.replace(ORIENTATION_PATTERN, '');
}

/**
 * Parses a Roman numeral string to its decimal value.
 * Returns null if parsing fails.
 */
export function parseRomanNumeral(roman: string): number | null {
  if (!roman) return null;

  const upperRoman = roman.toUpperCase();
  let result = 0;
  let prevValue = 0;

  for (let i = upperRoman.length - 1; i >= 0; i--) {
    const char = upperRoman[i];
    const currentValue = ROMAN_VALUES[char];

    if (currentValue === undefined) return null;

    if (currentValue < prevValue) {
      result -= currentValue;
    } else {
      result += currentValue;
    }
    prevValue = currentValue;
  }

  return result;
}

/**
 * Converts a decimal number to Roman numeral string.
 * Supports numbers 1-3999.
 */
export function numberToRoman(num: number): string | null {
  if (num < 1 || num > 3999) return null;

  const romanNumerals: [number, string][] = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']
  ];

  let result = '';
  let remaining = num;

  for (const [value, numeral] of romanNumerals) {
    while (remaining >= value) {
      result += numeral;
      remaining -= value;
    }
  }

  return result;
}

/**
 * Checks if a code represents a range (e.g., "X-XII").
 */
export function isRange(code: string): boolean {
  return RANGE_PATTERN.test(code);
}

/**
 * Expands a range code to individual Roman numerals.
 * E.g., "X-XII" -> ["X", "XI", "XII"]
 * Returns null if the range is invalid.
 */
export function expandRange(rangeCode: string): string[] | null {
  const match = rangeCode.match(RANGE_PATTERN);
  if (!match) return null;

  const startRoman = match[1].toUpperCase();
  const endRoman = match[2].toUpperCase();

  const startNum = parseRomanNumeral(startRoman);
  const endNum = parseRomanNumeral(endRoman);

  if (startNum === null || endNum === null) return null;
  if (startNum > endNum) return null;

  const result: string[] = [];
  for (let i = startNum; i <= endNum; i++) {
    const roman = numberToRoman(i);
    if (roman) result.push(roman);
  }

  return result;
}

/**
 * Parses a comma-separated location code field and expands all ranges.
 * Also strips orientation suffixes for matching purposes.
 * E.g., "X-XII, XVIIw" -> ["X", "XI", "XII", "XVII"]
 */
export function expandLocationCodes(locationCodeField: string | undefined): string[] {
  if (!locationCodeField) return [];

  const codes = locationCodeField.split(',').map(c => c.trim()).filter(c => c.length > 0);
  const result: string[] = [];

  for (const code of codes) {
    const strippedCode = stripOrientation(code);

    if (isRange(strippedCode)) {
      const expanded = expandRange(strippedCode);
      if (expanded) {
        result.push(...expanded);
      } else {
        // If range expansion fails, keep the original (for exact matching)
        result.push(strippedCode);
      }
    } else {
      result.push(strippedCode.toUpperCase());
    }
  }

  return result;
}

/**
 * Extracts the location suffix from a plan element ID.
 * E.g., "location-XI" -> "XI", "location-X-XII" -> "X-XII"
 */
export function extractLocationSuffix(elementId: string): string | null {
  const prefix = 'location-';
  if (!elementId.startsWith(prefix)) return null;
  return elementId.substring(prefix.length);
}

/**
 * Checks if a plan location element matches a location code field.
 *
 * Matching rules:
 * 1. Exact match: "location-X-XII" matches locationCode containing "X-XII"
 * 2. Range expansion: "location-XI" matches locationCode "X-XII" (expands to X, XI, XII)
 * 3. Orientation stripped: "location-XI" matches locationCode "XIw" (strips 'w')
 *
 * @param planLocationId The element ID from the SVG (e.g., "location-XI")
 * @param locationCodeField The comma-separated location code field (e.g., "X-XII, XVII")
 */
export function planLocationMatches(planLocationId: string, locationCodeField: string | undefined): boolean {
  if (!planLocationId || !locationCodeField) return false;

  const locationSuffix = extractLocationSuffix(planLocationId);
  if (!locationSuffix) return false;

  // Parse the location code field into individual codes (with ranges expanded)
  const rawCodes = locationCodeField.split(',').map(c => c.trim()).filter(c => c.length > 0);

  // First, check for exact match (e.g., location-X-XII matches "X-XII")
  for (const code of rawCodes) {
    const strippedCode = stripOrientation(code);
    if (strippedCode.toUpperCase() === locationSuffix.toUpperCase()) {
      return true;
    }
  }

  // Then, expand ranges and check if the location suffix matches any expanded code
  const expandedCodes = expandLocationCodes(locationCodeField);
  const normalizedSuffix = locationSuffix.toUpperCase();

  return expandedCodes.includes(normalizedSuffix);
}

/**
 * Gets all plan location IDs that should be highlighted for a given location code field.
 * This is the inverse of planLocationMatches - given a location code, find all matching plan elements.
 *
 * @param locationCodeField The comma-separated location code field (e.g., "X-XII, XVII")
 * @returns Array of location suffixes that should be highlighted (e.g., ["X", "XI", "XII", "XVII"])
 */
export function getHighlightedLocationSuffixes(locationCodeField: string | undefined): string[] {
  if (!locationCodeField) return [];

  const rawCodes = locationCodeField.split(',').map(c => c.trim()).filter(c => c.length > 0);
  const result: string[] = [];

  for (const code of rawCodes) {
    const strippedCode = stripOrientation(code);

    if (isRange(strippedCode)) {
      const expanded = expandRange(strippedCode);
      if (expanded) {
        result.push(...expanded);
      } else {
        // Keep as-is for exact matching (e.g., "X-XII" as a single location)
        result.push(strippedCode.toUpperCase());
      }
    } else {
      result.push(strippedCode.toUpperCase());
    }
  }

  return Array.from(new Set(result)); // Remove duplicates
}

